import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, BookOpen } from "lucide-react";
import {
  card,
  cardTitle,
  dividerInner,
  emptyState,
  iconSize,
  bodySecondary,
} from "../../../lib/design-system";
import { cn } from "../../../lib/utils";

export default function SyllabusAccordion({ course }) {
  const [isOpen, setIsOpen] = useState(true);
  const modules = course?.syllabus?.modules;

  return (
    <div className={cn(card, "overflow-hidden")}>
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between p-6 border-b transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50",
          dividerInner
        )}
      >
        <h3 className={cardTitle}>Syllabus</h3>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        )}
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.section
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="overflow-hidden"
          >
            <div className="p-6">
              {modules && modules.length > 0 ? (
                <div className="overflow-x-auto rounded-sm border border-gray-200 dark:border-gray-700">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-900">
                        <th
                          className={cn(
                            "border-b px-4 py-2.5 text-left text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400",
                            dividerInner
                          )}
                        >
                          Module
                        </th>
                        <th
                          className={cn(
                            "border-b px-4 py-2.5 text-left text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400",
                            dividerInner
                          )}
                        >
                          Title
                        </th>
                        <th
                          className={cn(
                            "border-b px-4 py-2.5 text-left text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400",
                            dividerInner
                          )}
                        >
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map((module, idx) => (
                        <tr
                          key={module.moduleNumber}
                          className={cn(
                            "hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors",
                            idx !== modules.length - 1 &&
                              cn("border-b", dividerInner)
                          )}
                        >
                          <td className="px-4 py-3 align-top w-20 text-sm font-medium text-gray-900 dark:text-white">
                            {module.moduleNumber}
                          </td>
                          <td className="px-4 py-3 align-top text-sm font-medium text-gray-800 dark:text-white">
                            {module.moduleTitle}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <p className={cn(bodySecondary, "leading-relaxed")}>
                              {Array.isArray(module.topics) &&
                              module.topics.length > 0
                                ? module.topics.join(" ")
                                : module.description || ""}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={emptyState}>
                  <BookOpen
                    className={cn(
                      iconSize.empty,
                      "text-gray-400 dark:text-gray-500 mx-auto mb-3"
                    )}
                  />
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    No Syllabus Available
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    The syllabus for this course has not been uploaded yet.
                  </p>
                </div>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
}
